import { useState, useCallback, useEffect, createContext, useContext } from 'react';

const STORAGE_KEY = 'vertex-hub-theme';

interface ThemeContextValue {
  isDark: boolean;
  toggle: () => void;
}

export const ThemeContext = createContext<ThemeContextValue>({
  isDark: true,
  toggle: () => {},
});

/** Access the current theme. */
export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}

/** Hook that manages dark/light theme state with localStorage persistence */
export function useThemeState(): ThemeContextValue {
  const [isDark, setIsDark] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    const dark = stored !== null ? stored === 'dark' : true;
    document.documentElement.dataset.theme = dark ? 'dark' : 'light';
    document.documentElement.style.colorScheme = dark ? 'dark' : 'light';
    return dark;
  });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, isDark ? 'dark' : 'light');
    const root = document.documentElement;
    root.dataset.theme = isDark ? 'dark' : 'light';
    root.style.colorScheme = isDark ? 'dark' : 'light';
  }, [isDark]);

  const toggle = useCallback(() => {
    setIsDark(prev => !prev);
  }, []);

  return { isDark, toggle };
}
